import {
  Info,
  UserCheck,
  Lock,
  ListChecks,
  Briefcase,
  CreditCard,
  Ban,
  XCircle,
  Scale,
  RefreshCcw,
} from "lucide-react";
import {
  legalTheme,
  ScrollProgressRail,
  LegalHero,
  SectionNav,
  MobileSectionPills,
  LegalSection,
  ContactSection,
  useActiveSection,
} from "../components/legal/LegalKit";

const sections = [
  {
    id: "overview",
    no: 1,
    title: "Overview",
    icon: Info,
    paragraphs: [
      "These Terms of Service provide an overview of how you may access and use the website and services operated by UniBcomp Networks Private Limited.",
      "By using our website or engaging our services, you agree to act responsibly, comply with applicable laws, and respect other users. These terms are designed to promote safe, lawful, and respectful use of our website and services.",
    ],
  },
  {
    id: "eligibility",
    no: 2,
    title: "Eligibility",
    icon: UserCheck,
    paragraphs: [
      "You must be at least 18 years old, or accessing our website under the supervision of a parent or guardian, to use our services or submit any enquiries, applications, or payments.",
    ],
  },
  {
    id: "account-responsibility",
    no: 3,
    title: "Account Responsibility",
    icon: Lock,
    paragraphs: [
      "Where an account, client portal, or similar access is provided to you, you are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account.",
    ],
  },
  {
    id: "acceptable-use",
    no: 4,
    title: "Acceptable Use",
    icon: ListChecks,
    paragraphs: [
      "You agree not to misuse our website or services, attempt unauthorized access to our systems, or engage in any activity that violates applicable laws or this policy.",
    ],
  },
  {
    id: "service-use",
    no: 5,
    title: "Service Use",
    icon: Briefcase,
    paragraphs: [
      "Services engaged through UniBcomp are provided for the purposes agreed upon between you and UniBcomp, as set out in the applicable proposal, agreement, or invoice. Misuse or fraudulent use of our services is prohibited.",
    ],
  },
  {
    id: "payments",
    no: 6,
    title: "Payments",
    icon: CreditCard,
    paragraphs: [
      "Payments for our services must be made using approved payment methods. You agree to provide accurate billing information and to make payments in accordance with the agreed terms.",
    ],
  },
  {
    id: "prohibited-activities",
    no: 7,
    title: "Prohibited Activities",
    icon: Ban,
    items: [
      "Uploading malicious code or attempting to disrupt our website or systems",
      "Providing false or misleading information",
      "Engaging in unlawful or fraudulent activity",
      "Violating intellectual property rights",
    ],
  },
  {
    id: "suspension-termination",
    no: 8,
    title: "Suspension or Termination",
    icon: XCircle,
    paragraphs: [
      "Access to our website, client portal, or services may be suspended or terminated if you violate these Terms of Service or any applicable policy.",
    ],
  },
  {
    id: "governing-law",
    no: 9,
    title: "Governing Law",
    icon: Scale,
    paragraphs: [
      "These Terms of Service are governed by the laws of India.",
    ],
  },
  {
    id: "policy-updates",
    no: 10,
    title: "Policy Updates",
    icon: RefreshCcw,
    paragraphs: [
      "UniBcomp may update these Terms of Service from time to time. Continued use of our website or services constitutes acceptance of the updated terms.",
    ],
  },
];

const sectionIds = [...sections.map((s) => s.id), "contact-us"];

export default function TermsOfService() {
  const activeId = useActiveSection(sectionIds);

  return (
    <div className="w-full" style={{ backgroundColor: legalTheme.surface }}>
      <ScrollProgressRail />

      <LegalHero
        eyebrow="Legal"
        title={["Terms of", "Service"]}
        HeroIcon={Scale}
        meta={
          <>
            <span className="inline-flex items-center gap-2">
              <Scale size={15} style={{ color: legalTheme.gold }} />
              Last Updated: July 2026
            </span>
            <span className="hidden sm:inline">·</span>
            <span>UniBcomp Networks Private Limited</span>
          </>
        }
        description="These Terms of Service outline the rules and guidelines for using the UniBcomp website and engaging our services. By accessing or using our services, you agree to comply with these terms."
        agreementNote="For questions regarding these terms, please contact us at support@unibcomp.net."
      />

      <MobileSectionPills sections={sections} activeId={activeId} />

      <section className="relative py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[240px_1fr] gap-16">
          <SectionNav sections={sections} activeId={activeId} />

          <div className="min-w-0 max-w-3xl">
            {sections.map((section) => (
              <LegalSection key={section.id} section={section} />
            ))}

            <ContactSection
              no={11}
              intro="For any questions regarding these Terms of Service, please contact us:"
              cards={[
                {
                  label: "General Enquiries",
                  desc: "For any questions regarding these Terms of Service.",
                  email: "support@unibcomp.net",
                },
              ]}
            />

            <div className="mt-8 pt-8 border-t text-sm" style={{ borderColor: legalTheme.border, color: legalTheme.inkMuted }}>
              © 2026 UniBcomp Networks Private Limited. All rights reserved.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}